<template>
  <section>
    <div id="wage" class="modal-card" style="width: auto">
      <header class="modal-card-head footerSourceModal-head">
        <p class="modal-card-title footerSourceModal-title">FAQ</p>
      </header>
      <section id="printBody" ref="PrintBody" class="modal-card-body footerSourceModal">
        <div v-html="$t('faq.faq_wage')"></div>
        <hr>
        <div v-html="$t('faq.faq_general')"></div>
      </section>
      <footer class="modal-card-foot footerSourceModal-foot">
        <b-button class="button" @click="print()">{{$t('faq.print_text')}}</b-button>
        <b-button class="button" @click="$parent.close()">{{$t('faq.close_text')}}</b-button>
      </footer>
    </div>
  </section>
</template>

<script>
import Vue from "vue";
import { Modal} from "buefy";
import Printd from "printd";
import VueI18n from "vue-i18n";

Vue.use(VueI18n);
Vue.use(Modal);
// Create VueI18n instance with options
export const i18n = new VueI18n({
  locale: 'en',
})

export default {
  i18n: {
    locale: 'en',
    messages: {
      en: {
        faq: {
          faq_general: `
            <h2 class="is-size-4">General</h2>
            <h3 class="is-size-5">Why are certain industries named and grouped the way they are?</h3>
            <p>
              The information presented here is based on the most recent (2017) Canadian version of the 
              <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank">North American Industry Classification System</a>. We use the 
              standard titles, definitions, and examples provided in the <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank">NAICS handbook</a>.
            </p>
            <h3 class="is-size-5">Why can't I find information for my industry and/or location?</h3>
            <p>
              The information may not exist in the combination you've chosen. For example, poppy flowers are not endemic to Canada so poppy farming is not likely to yield 
              results in any province selected.
            </p>
            <p>
              Sometimes an industry is represented by such a small number of businesses in a certain location that we can't share the data without violating the privacy of 
              those businesses. Other times, the information may be inaccurate or missing. In these cases, Statistics Canada has flagged the data as unreliable and we have 
              removed it from our collection.
            </p>
            <h3 class="is-size-5">Why am I being shown a different industry and/or location than the one I chose?</h3>
            <p>
              We always show the most detailed and specific information available. However, if information is not available for your choices, the program will show the most 
              similar industry or location for which information is available.
            </p>
            <p>
              In the case of location, this often means showing you national instead of provincial information.
            </p>
            <p>
              In the case of industry, this often means showing you information from a broader sector instead of the specific industry you chose. For example, we may not 
              have information on resorts in particular but we have information on the traveller accommodation sector (which includes resorts, hotels, motels, and motor 
              homes).
            </p>`,
            faq_wage: `
            <h2 class="is-size-4">Median Hourly Full-time Wage</h2>
            <h3 class="is-size-5">What is the wage data for?</h3>
            <p>
              The wage data widget is intended to provide insight into the hourly wage for a given occupation within economic regions of Canada. The dataset we use is 
              prepared by the Labour Market Data, Methods and Analysis Division of Employment and Social Development Canada (ESDC) and it is used in the trends and analysis 
              section of the Government of Canada’s <a href="https://www.jobbank.gc.ca/trend-analysis" target="_blank">Job Bank website</a>.
            </p>
            <p>
              The median is used for hourly wage for an occupation. The median wage is preferred over the average wage because it is less sensitive to extreme values and 
              more representative of the typical worker's salary.
            </p>
            <p>
              Occupations are classified using the National Occupation Classification system at the four-digit level.
            </p>
            <p>
              Location data is presented by the Economic Region, a variant of the Standard Geographical Region statistical classification system created by Statistics Canada 
              as a standard geographic unit for the analysis of economic activity within 76 regions across Canada. Location in our widget are mapped to the broader economic 
              region to which they belong.
            </p>
            <h3 class="is-size-5">Which sources of data are used?</h3>
            <p>
              Wages are determined using a joint <abbr title="Employment and Social Development Canada">ESDC</abbr> and Statistics Canada methodology. 
              <abbr title="Employment and Social Development Canada">ESDC</abbr> consults other stakeholders during the validation process, such as provincial and territorial 
              governments, other federal departments and external organizations.
            </p>
            <p>
              As the primary source of the dataset, <abbr title="Employment and Social Development Canada">ESDC</abbr> uses Statistics Canada's Labour Force Survey:
            </p>
            <ul>
              <li>the most inclusive, timely and unbiased source of wage data by occupational group</li>
              <li>this survey is conducted using a sample of respondents</li>
              <li>data may be suppressed for reasons of confidentiality or data quality</li>
            </ul>
            <p>
              When the Labour Force Survey data are not available, other sources are considered, including:
            </p>
            <ul>
              <li><abbr title="Employment and Social Development Canada">ESDC</abbr> program data such as Employment Insurance program survey data</li>
              <li>the Census</li>
              <li>provincial and territorial surveys and administrative data</li>
              <li>other organizations administrative data</li>
            </ul>
            <p>
              For the complete process followed by <abbr title="Employment and Social Development Canada">ESDC</abbr>, please see their 
              <a href="https://www.jobbank.gc.ca/trend-analysis/search-wages/wage-methodology" target="_blank">Wage Methodology</a>.
            </p>
            <h3 class="is-size-5">Have you manipulated the original dataset?</h3>
            <p>
              We have removed from the dataset salaries that were presented as an annual salary versus an hourly wage. However, this data can be found from the original source.
            </p>
            <h3 class="is-size-5">Why is there no data available for some combinations of occupation and location?</h3>
            <p>
              It is not always possible to determine occupational wages for all economic regions due to data limitations (suppression of data due to low quality and privacy 
              reasons). When this is the case, we will display the minimum wage of the province or territory. When comparing locations or industries, these values will appear 
              as not available (n/a) and without the minimum wage.
            </p>
            <h3 class="is-size-5">How are you able to pull occupational data by searching for an industry?</h3>
            <p>
              We have done an extensive analysis to develop a mapping of occupations by industries. Through concordance tables, we are able to link wage data by occupation to 
              their related industries.
            </p>
            <h3 class="is-size-5">Which datasets is used by the widget?</h3>
            <p>
              Our widget is using the <abbr title="Employment and Social Development Canada">ESDC</abbr> 2019 Wages Dataset, which can be downloaded from the Government of Canada's <a href="https://open.canada.ca/data/en/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">Open Data website</a>.
            </p>
            <h3 class="is-size-5">How soon is the widget updated after a new wage data version is released?</h3>
            <p>
              We aim to update the data within 3 months of the new release of new dataset. This dataset is produced yearly, and it is published in the late fall.
            </p>`,
          print_text: "Print",
          close_text: "Close"
        }
      },
      fr: {
        faq: {
          faq_general: `
            <h2 class="is-size-4">Générale</h2>
            <h3 class="is-size-5">Pourquoi certains secteurs d'activités sont nommés et groupés de cette façon?</h3>
            <p>
              L'information présenté ici est basé sur la version canadienne la plus récente (2017) du 
              <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">Système de classification des industries de l'Amérique du Nord</a>. 
              Nous utilisons les titres, les définitions et les exemples standards fourni dans le 
              <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">guide du SCIAN</a>.
            </p>
            <h3 class="is-size-5">Pourquoi je ne peux trouver l'information pour mon secteur d'activité et/ou l'emplacement?</h3>
            <p>
              L'information peut ne pas existé dans la combinaison que vous avez choisi. Par exemple, le coquelicot n'est pas une espèce endémique du Canada donc l'agriculture 
              du coquelicot ne produirait probablement pas des résultats dans l'une ou l'autre des provinces sélectionnées.
            </p>
            <p>
              Parfois un secteur d'activité est représenté par un faible nombre d'entreprises dans un certain emplacement que nous ne pouvons pas partager les données sans 
              faire abstraction à la protection de la vie privée de ses entrprises. En d'autre temps, l'information peut être inexacte ou manquant. Dans ces cas, Statistique 
              Canada a signalé les données comme un fonctionnement incertain et nous les avons retiré de notre collection.
            </p>
            <h3 class="is-size-5">Pourquoi je suis montré un secteur d'activité et/ou emplacement différent de ce que j'ai choisi?</h3>
            <p>
              Nous montrons toujours l'information la plus détaillé et spécifique disponible. Cependant, si l'information n'est pas disponible pour vos choix, le programme va 
              montrer le secteur d'activité ou l'emplacement le plus similaire dont l'information est disponible.
            </p>
            <p>
              Dans le cas de l'emplacement, ceci veut souvent dire qu'on vous montre l'information national au lieu de provincial.
            </p>
            <p>
              Dans le cas du secteur d'activité, ceci veut souvent dire qu'on vous montre l'information à partir d'un secteur plus générale au lieu d'un secteur d'activité 
              spécifique que vous avez choisi. Par exemple, nous n'avons peut-être pas l'information sur les centres de villégiature en particulier mais nous avons 
              l'information sur le secteur de l'hébergement des voyageurs (qui inclus centres de villégiature, hôtels, motels et maisons motorisées).
            </p>`,
            faq_wage: `
            <h2 class="is-size-4">Salaire horaire médian à temps plein</h2>
            <h3 class="is-size-5">Quelle est l'utilité des données sur les salaires?</h3>
            <p>
              Le widget de données sur les salaires vise à donner un aperçu du salaire horaire pour une profession donnée dans les régions économiques du Canada.
            </p>
            <p>
              Le jeu de données que nous utilisons est préparé par la Division des données, Méthodes et analyse du marché du travail d'Emploi et Développement social Canada (EDSC) 
              où il est utilisé dans la section d’analyse des tendances du <a href="https://www.guichetemplois.gc.ca/analyse-tendances" target="_blank">site Web du Guichet 
              emplois</a> du gouvernement du Canada.
            </p>
            <p>
              La médiane est utilisée pour le salaire horaire d'une profession. Le salaire médian est préféré au salaire moyen car il est moins sensible aux valeurs extrêmes et 
              plus représentatif du salaire type du travailleur.
            </p>
            <p>
              Les professions sont classées à l'aide du système de Classification nationale des professions au niveau des codes à quatre chiffres.
            </p>
            <p>
              Les données d’emplacement sont présentées par région économique, une variante du système de Classification géographique type, normalisées créé par Statistique 
              Canada en tant qu'unité géographique standard pour l'analyse de l'activité économique dans 76 régions du Canada. Les emplacements dans notre widget sont mappés 
              à la région économique plus large à laquelle ils appartiennent.
            </p>
            <h3 class="is-size-5">Quelles sources de données sont utilisées?</h3>
            <p>
              Les salaires sont déterminés à l'aide d'une méthodologie conjointe d'<abbr title="Emploi et Développement social Canada">EDSC</abbr> et de Statistique Canada. 
              <abbr title="Emploi et Développement social Canada">EDSC</abbr> consulte d'autres intervenants au cours du processus de validation, comme les gouvernements 
              provinciaux et territoriaux, d'autres ministères fédéraux et des organismes externes.
            </p>
            <p>
              En tant que source principale du jeu de données, <abbr title="Emploi et Développement social Canada">EDSC</abbr> utilise l'Enquête sur la population active 
              de Statistique Canada :
            </p>
            <ul>
              <li>la source de données sur les salaires par groupe professionnel la plus inclusive, opportune et impartiale;</li>
              <li>cette enquête est menée auprès d'un échantillon de répondants;</li>
              <li>les données peuvent être supprimées pour des raisons de confidentialité ou de qualité des données.</li>
            </ul>
            <p>
              Lorsque les données de l'Enquête sur la population active ne sont pas disponibles, d'autres sources sont envisagées, notamment:
            </p>
            <ul>
              <li>les données du programme d'<abbr title="Emploi et Développement social Canada">EDSC</abbr>, comme les données d'enquête sur le programme d'assurance-emploi;</li>
              <li>le recensement;</li>
              <li>les enquêtes provinciales et territoriales et les données administratives;</li>
              <li>autres données administratives des organisations.</li>
            </ul>
            <p>
              Pour le processus complet suivi par <abbr title="Emploi et Développement social Canada">EDSC</abbr>, veuillez consulter leur 
              <a href="https://www.guichetemplois.gc.ca/analyse-tendances/recherche-salaires/methodologie-salaires" target="_blank">méthodologie des salaires</a>.
            </p>
            <h3 class="is-size-5">Avez-vous manipulé le jeu de données d'origine?</h3>
            <p>
              Nous avons supprimé du jeu de données les salaires qui étaient présentés comme un salaire annuel au lieu d’un salaire horaire. Cependant, ces données peuvent 
              être trouvées à partir de la source d'origine.
            </p>
            <h3 class="is-size-5">Pourquoi n'y a-t-il pas de données disponibles pour certaines combinaisons de profession et d’emplacement?</h3>
            <p>
              Il n'est pas toujours possible de déterminer les salaires des professions pour toutes les régions économiques en raison des limites des données (suppression 
              des données pour des raisons de faible qualité et de confidentialité). Lorsque c'est le cas, nous afficherons le salaire minimum de la province ou du territoire. 
              Lorsque vous comparez des emplacements ou des secteurs d’activités, ces valeurs non disponibles afficheront comme étant sans objet (s.o.) et sans le salaire minimum.
            </p>
            <h3 class="is-size-5">Comment pouvez-vous extraire des données sur les professions en recherchant un secteur d’activité?</h3>
            <p>
              Nous avons effectué une analyse approfondie pour élaborer une cartographie des professions par secteur d’activités. Grâce à des tableaux de concordance, 
              nous pouvons relier les données sur les salaires par profession à leurs secteurs d’activités connexes.
            </p>
            <h3 class="is-size-5">Quel jeu de données est utilisés par le widget?</h3>
            <p>
              Notre widget utilise le jeu de données sur les salaires d'<abbr title="Emploi et Développement social Canada">EDSC</abbr> 2019, qui peut être téléchargé à partir 
              du <a href="https://ouvert.canada.ca/data/fr/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">site Web de données ouvertes</a> du gouvernement du Canada.
            </p>
            <h3 class="is-size-5">Combien de temps le widget est-il mis à jour suite à la publication d'une nouvelle version des données sur les salaires?</h3>
            <p>
              Notre objectif est de mettre à jour les données dans les 3 mois suivant la nouvelle version du nouveau jeu de données. Ce jeu de données est produit chaque année 
              et publié à la fin de l'automne.
            </p>`,
          print_text: "Imprimer",
          close_text: "Fermer"
        }
      }
    }
  }, // end i18n
  props: [
    'lang',
  ],
  mounted: function() {
    this.$i18n.locale = this.toggleLang;
  },
  computed: {
    toggleLang: function() {
      return this.lang;
    }
  }, // end computed
  methods: {
    print: function() {
      const d = new Printd();
      d.print(this.$refs.PrintBody);
    }
  } // end methods
}
</script>
